import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Building2, Mail, Phone, Store, MapPin, Eye } from "lucide-react";

const APPROVAL_COLORS = {
  approved: "bg-green-500/10 text-green-600 border-green-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  rejected: "bg-red-500/10 text-red-600 border-red-500/30",
  suspended: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  active: "bg-green-500/10 text-green-600 border-green-500/30",
};

/**
 * Admin tab listing every franchise / multi-location owner account along
 * with the stores under each owner. Per-store admin actions (approve,
 * suspend, edit) continue to live in the regular Shops tab — the cross-
 * reference here is enough for the admin to navigate.
 */
export default function AdminFranchiseOwnersTab({ franchiseOwners, onImpersonateOwner }) {
  if (!franchiseOwners || franchiseOwners.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center space-y-1">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="font-medium">No multi-location accounts yet</p>
          <p className="text-sm text-muted-foreground">
            Franchise owners that register via the shop sign-up flow will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-franchise-owners-list">
      {franchiseOwners.map((owner) => (
        <Card key={owner.owner_id} data-testid={`admin-owner-${owner.owner_id}`}>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  <h3 className="font-heading font-semibold">{owner.business_name}</h3>
                  <Badge className={APPROVAL_COLORS[owner.account_status] || APPROVAL_COLORS.pending}>
                    {owner.account_status}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">Owner: {owner.owner_name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{owner.email}</span>
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{owner.phone}</span>
                  <span className="flex items-center gap-1"><Store className="h-3 w-3" />{owner.store_count || 0} {owner.store_count === 1 ? "store" : "stores"}</span>
                </div>
              </div>
              {onImpersonateOwner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onImpersonateOwner(owner.owner_id)}
                  data-testid={`impersonate-owner-${owner.owner_id}`}
                  title="View this owner's Franchise Dashboard read-only (15 min)"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View as Owner
                </Button>
              )}
            </div>

            {/* Linked stores */}
            {owner.stores && owner.stores.length > 0 && (
              <div className="border-t pt-3 space-y-1.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Stores</p>
                {owner.stores.map((store) => (
                  <div key={store.shop_id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <Store className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{store.shop_name}</span>
                      {store.address?.suburb && (
                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />{store.address.suburb}
                        </span>
                      )}
                    </div>
                    <Badge className={APPROVAL_COLORS[store.approval_status] || APPROVAL_COLORS.pending}>
                      {store.approval_status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
